<div class="page-container">
  <!-- Page Header -->
  <app-page-header title="Manage Owners" subtitle="Manage radio station owners and their properties"
    [showBackButton]="true" backRoute="/userhome" backLabel="Back to Dashboard">
    <button class="btn-primary" (click)="openAddModal()">
      <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
      </svg>
      Add New Owner
    </button>
  </app-page-header>

  <!-- Owners Table -->
  <div class="content-wrapper">
    <app-common-table [data]="owners" [columns]="tableColumns" [actions]="tableActions" [loading]="loading"
      emptyMessage="No owners found" emptyDescription="Get started by adding your first radio station owner."
      [showAddButton]="true" addButtonText="Add New Owner" (addClick)="openAddModal()">

      <!-- Changed template reference from #tableRow to #rowTemplate to match common table component -->
      <ng-template #rowTemplate let-owner>
        <tr class="table-row">
          <td class="td-id">
            <span class="owner-id-pill">{{ owner.id }}</span>
          </td>
          <td class="td-name">
            <div class="owner-name-cell">
              <div class="owner-avatar" aria-hidden="true">
                {{ ownerInitial(owner) }}
              </div>
              <div class="owner-info">
                <div class="owner-name" [attr.title]="owner.name">{{ owner.name }}</div>
                <div class="station-count">
                  {{ owner.station_count > 0
                  ? (owner.station_count + (owner.station_count === 1 ? ' station' : ' stations'))
                  : 'No stations' }}
                </div>
              </div>
            </div>
          </td>
          <td class="td-stations">
            <div *ngIf="owner.station_call_signs?.length; else noStations" class="station-list">
              <span *ngFor="let callSign of owner.station_call_signs" class="station-badge">
                {{ callSign }}
              </span>
            </div>
            <ng-template #noStations>
              <span class="no-data">No Stations Yet</span>
            </ng-template>
          </td>
          <td class="td-date">
            <span class="date-text">{{ formatDate(owner.created_at) }}</span>
          </td>
          <td class="td-actions">
            <div class="action-buttons">
              <button type="button" class="btn-edit" (click)="openEditModal(owner)" title="Edit owner"
                aria-label="Edit owner">
                <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
                  </path>
                </svg>
              </button>
              <button type="button" class="btn-delete" (click)="deleteOwner(owner)" title="Delete owner"
                aria-label="Delete owner">
                <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16">
                  </path>
                </svg>
              </button>
            </div>
          </td>
        </tr>
      </ng-template>
    </app-common-table>

    <div class="pagination-panel" *ngIf="!loading && totalItems > 0">
      <p class="pagination-summary">Showing {{ paginationStart }}–{{ paginationEnd }} of {{ totalItems }} owners</p>
    </div>
    <div *ngIf="totalPages > 1" class="pagination" role="navigation" aria-label="Owners pagination">
      <button type="button" class="pagination-btn" [class.disabled]="currentPage === 1"
        (click)="goToPage(currentPage - 1)" [disabled]="currentPage === 1">
        Previous
      </button>

      <div class="pagination-numbers">
        <button type="button" *ngFor="let page of getPaginationPages(); trackBy: trackByPage" class="pagination-number"
          [class.active]="page === currentPage" (click)="goToPage(page)">
          {{ page }}
        </button>
      </div>

      <button type="button" class="pagination-btn" [class.disabled]="currentPage === totalPages"
        (click)="goToPage(currentPage + 1)" [disabled]="currentPage === totalPages">
        Next
      </button>
    </div>
  </div>
</div>

<!-- Modal -->
<div *ngIf="isModalOpen" class="modal-overlay" (click)="closeModal()">
  <div class="modal-content" (click)="$event.stopPropagation()" role="dialog" aria-modal="true"
    [attr.aria-label]="isEditMode ? 'Edit Owner' : 'Add New Owner'">
    <div class="modal-header">
      <div>
        <h2 class="modal-title">{{ isEditMode ? 'Edit Owner' : 'Add New Owner' }}</h2>
        <p class="modal-subtitle">
          {{ isEditMode ? 'Update company details and branding logos.' : 'Create an owner account and optional logos.'
          }}
        </p>
      </div>
      <button type="button" class="modal-close" (click)="closeModal()" aria-label="Close">
        <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>

    <form [formGroup]="ownerForm" (ngSubmit)="onSubmit()" class="modal-form">
      <section class="form-section">
        <header class="section-head">
          <span class="section-icon" aria-hidden="true">
            <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4">
              </path>
            </svg>
          </span>
          <div>
            <h3 class="section-title">Owner Information</h3>
            <p class="section-subtitle">Company identity and contact details</p>
          </div>
        </header>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label" for="name">Company Name *</label>
            <input type="text" id="name" class="form-input" formControlName="name" placeholder="Enter company name"
              maxlength="100">
            <div *ngIf="ownerForm.get('name')?.hasError('required') && ownerForm.get('name')?.touched"
              class="form-error">
              Company name is required
            </div>
          </div>

          <div *ngIf="!isEditMode" class="form-group">
            <label class="form-label" for="email">Email *</label>
            <input type="email" id="email" class="form-input" formControlName="email" placeholder="Enter email address">
            <div *ngIf="ownerForm.get('email')?.hasError('required') && ownerForm.get('email')?.touched"
              class="form-error">
              Email is required
            </div>
            <div *ngIf="ownerForm.get('email')?.hasError('email') && ownerForm.get('email')?.touched"
              class="form-error">
              Please enter a valid email address
            </div>
          </div>

          <div *ngIf="!isEditMode" class="form-group">
            <label class="form-label" for="firstname">First Name *</label>
            <input type="text" id="firstname" class="form-input" formControlName="firstname"
              placeholder="Enter first name">
            <div *ngIf="ownerForm.get('firstname')?.hasError('required') && ownerForm.get('firstname')?.touched"
              class="form-error">
              First name is required
            </div>
          </div>

          <div *ngIf="!isEditMode" class="form-group">
            <label class="form-label" for="lastname">Last Name *</label>
            <input type="text" id="lastname" class="form-input" formControlName="lastname"
              placeholder="Enter last name">
            <div *ngIf="ownerForm.get('lastname')?.hasError('required') && ownerForm.get('lastname')?.touched"
              class="form-error">
              Last name is required
            </div>
          </div>

          <div *ngIf="isEditMode && selectedOwner?.user_details as users" class="readonly-info">
            <div class="readonly-field">
              <label class="readonly-label">Contact Person</label>
              <span class="readonly-value">{{ users[0].firstname }} {{ users[0].lastname }}</span>
            </div>
            <div class="readonly-field">
              <label class="readonly-label">Email</label>
              <span class="readonly-value">{{ users[0].email }}</span>
            </div>
          </div>
        </div>
      </section>

      <section class="form-section">
        <header class="section-head">
          <span class="section-icon" aria-hidden="true">
            <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z">
              </path>
            </svg>
          </span>
          <div>
            <h3 class="section-title">Logo Files</h3>
            <p class="section-subtitle">Optional branding for light and dark surfaces</p>
          </div>
        </header>

        <div class="logo-grid">
          <div class="form-group">
            <label class="form-label" for="logo">Logo</label>
            <div *ngIf="logoPreviewUrl || existingLogoUrl" class="owner-img-preview">
              <div class="owner-img-preview__label">Logo Preview</div>
              <div class="owner-img-preview__box">
                <img [src]="logoPreviewUrl || existingLogoUrl" alt="Owner logo preview" class="owner-img-preview__img"
                  (error)="onPreviewImageError('logo')">
                <button type="button" class="owner-img-preview__clear" (click)="clearLogoPreview(logoInput)"
                  title="Remove logo" aria-label="Remove logo">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                    aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            </div>

            <div class="owner-upload-zone" [class.owner-upload-zone--has-preview]="logoPreviewUrl || existingLogoUrl"
              [class.owner-upload-zone--dragging]="dragOverField === 'logo'" (dragover)="onDragOver($event, 'logo')"
              (dragleave)="onDragLeave('logo')" (drop)="onLogoDrop($event, logoInput)">
              <input #logoInput type="file" id="logo" class="file-input-hidden" accept="image/*"
                (change)="onLogoFileSelected($event)">
              <label for="logo" class="owner-upload-zone__label">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"
                  aria-hidden="true">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                <span *ngIf="!logoPreviewUrl && !existingLogoUrl">
                  Click or drop to upload logo<br>
                  <small>PNG, JPG, SVG recommended</small>
                </span>
                <span *ngIf="logoPreviewUrl || existingLogoUrl">Click or drop to replace logo</span>
              </label>
            </div>
            <small class="form-help" *ngIf="isUploadingLogo">Uploading logo…</small>
          </div>

          <div class="form-group">
            <label class="form-label" for="inverted_logo">Inverted Logo</label>
            <div *ngIf="invertedLogoPreviewUrl || existingInvertedLogoUrl" class="owner-img-preview">
              <div class="owner-img-preview__label">Inverted Logo Preview</div>
              <div class="owner-img-preview__box owner-img-preview__box--inverted">
                <img [src]="invertedLogoPreviewUrl || existingInvertedLogoUrl" alt="Inverted owner logo preview"
                  class="owner-img-preview__img" (error)="onPreviewImageError('inverted_logo')">
                <button type="button" class="owner-img-preview__clear"
                  (click)="clearInvertedLogoPreview(invertedLogoInput)" title="Remove inverted logo"
                  aria-label="Remove inverted logo">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                    aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            </div>

            <div class="owner-upload-zone"
              [class.owner-upload-zone--has-preview]="invertedLogoPreviewUrl || existingInvertedLogoUrl"
              [class.owner-upload-zone--dragging]="dragOverField === 'inverted_logo'"
              (dragover)="onDragOver($event, 'inverted_logo')" (dragleave)="onDragLeave('inverted_logo')"
              (drop)="onInvertedLogoDrop($event, invertedLogoInput)">
              <input #invertedLogoInput type="file" id="inverted_logo" class="file-input-hidden" accept="image/*"
                (change)="onInvertedLogoFileSelected($event)">
              <label for="inverted_logo" class="owner-upload-zone__label">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"
                  aria-hidden="true">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                <span *ngIf="!invertedLogoPreviewUrl && !existingInvertedLogoUrl">
                  Click or drop to upload inverted logo<br>
                  <small>For dark backgrounds</small>
                </span>
                <span *ngIf="invertedLogoPreviewUrl || existingInvertedLogoUrl">Click or drop to replace inverted
                  logo</span>
              </label>
            </div>
            <small class="form-help" *ngIf="isUploadingInvertedLogo">Uploading inverted logo…</small>
          </div>
        </div>
      </section>

      <div class="form-section">
        <h3 class="section-title">Owner Terms</h3>
        <div class="form-group form-group-full">
          <label class="form-label" for="order_terms">Terms (optional)</label>
          <textarea id="order_terms" class="form-input form-textarea" formControlName="order_terms" rows="6"
            [attr.maxlength]="orderTermsMaxLength"
            placeholder="Optional terms shown to advertisers before they place an order (e.g. pricing methodology, impression calculation, station policies)."></textarea>
          <small class="form-help">
            Leave empty if this owner has no special order terms. Advertisers must acknowledge these terms before
            payment.
            {{ ownerForm.get('order_terms')?.value?.length || 0 }} / {{ orderTermsMaxLength }}
          </small>
          <div *ngIf="ownerForm.get('order_terms')?.hasError('maxlength')" class="form-error">
            Terms cannot exceed {{ orderTermsMaxLength }} characters
          </div>
        </div>
      </div>

      <div class="modal-actions">
        <button type="button" class="btn-secondary" (click)="closeModal()">Cancel</button>
        <button type="submit" class="btn-primary"
          [disabled]="!ownerForm.valid || isSubmitting || isLoadingOwner || isUploadingLogo || isUploadingInvertedLogo">
          <span *ngIf="isSubmitting || isLoadingOwner" class="spinner-small"></span>
          {{ isLoadingOwner
          ? 'Loading...'
          : (isSubmitting
          ? 'Processing...'
          : (isEditMode ? 'Update Owner' : 'Create Owner')) }}
        </button>
      </div>
    </form>
  </div>
</div>